/* @flow */

import { CompulsoryTrackingTags } from "../tracking/Tracking";
import { trackStructuredEvent } from "../utils/TrackingUtil";
import { ReplacementKind, TrackingTags } from "./Tracking";

const nameString = (eventName: string) => "lists." + eventName;

export function trackCreateListFromOrder(orderId: string, listType: string, name: string) {
    trackStructuredEvent(nameString("createFromOrder"), {
        tags: {
            orderId,
            listType,
            name
        }
    });
}

// DEPRECATED: Only used in old articlesearch-ui components anymore
export function trackAddItemToList(
    originEventId: string,
    bettyBundleId: string,
    trackingId: string,
    pageType: string,
    zeroResultType: string | undefined | null,
    inContainer: boolean,
    listType: string,
    listId: string,
    name: string,
    marketplace: boolean,
    offerId: string | undefined | null,
    ownBrand: string | undefined | null,
    ropoRank: number | undefined | null
) {
    const tags = {
        originEventId,
        bettyBundleId,
        trackingId,
        pageType,
        zeroResultType,
        inContainer,
        listType,
        listId,
        name,
        marketplace,
        offerId,
        ownBrand,
        ropoRank
    };
    trackStructuredEvent(nameString("add"), { tags: tags });
}

export function trackAddItemToListNew(
    bettyBundleId: string,
    bettyVariantId: string,
    listType: string,
    listId: string,
    name: string, // name of the list
    compulsoryTrackingTags: CompulsoryTrackingTags,
    optionalTrackingTags?: Partial<TrackingTags>
) {
    const tags = {
        ...optionalTrackingTags,
        originEventId: compulsoryTrackingTags?.originEventId || "",
        pageType: compulsoryTrackingTags?.pageType || "",
        bettyBundleId,
        bettyVariantId,
        listType,
        listId,
        name
    };
    trackStructuredEvent(nameString("add"), { tags: tags });
}

export function trackAddItemToListGA(bettyBundleId: string) {
    trackStructuredEvent("manual.event", {
        tags: {
            eventCategory: "eCommerce",
            eventAction: "addToList",
            eventLabel: bettyBundleId
        }
    });
}

export function trackToggleCategory(listType: string, listId: string, categoryName: string, toggleState: boolean) {
    trackStructuredEvent(nameString("toggleCategory"), {
        tags: {
            listType,
            listId,
            categoryName,
            toggleState
        }
    });
}

export function trackChangeListSorting(listType: string, listId: string, sort: string) {
    trackStructuredEvent(nameString("changeListSorting"), {
        tags: {
            listType,
            listId,
            sort
        }
    });
}

export function trackListSearch(listType: string, listId: string, query: string) {
    const sanitizedQuery = query ? query.toLowerCase() : query;
    trackStructuredEvent(nameString("listSearch"), {
        tags: {
            listType,
            listId,
            query: sanitizedQuery
        }
    });
}

export function trackChangeName(listType: string, listId: string, name: string) {
    trackStructuredEvent(nameString("changeName"), {
        tags: {
            listType,
            listId,
            name
        }
    });
}

export function trackChangeVisibility(listType: string, listId: string, globallyVisible: boolean) {
    trackStructuredEvent(nameString("changeVisibility"), {
        tags: {
            listType,
            listId,
            globallyVisible
        }
    });
}

export function trackChangeReadOnlyForEmployees(listType: string, listId: string, readOnlyForEmployees: boolean) {
    trackStructuredEvent(nameString("changeReadOnlyForEmployees"), {
        tags: {
            listType,
            listId,
            readOnlyForEmployees
        }
    });
}

export function trackPersonallistImpression(
    listId: string,
    listType: string,
    name: string,
    nrItems: number,
    sort: string,
    isRecentlyBoughtList: boolean,
    isCipList: boolean
) {
    trackStructuredEvent("list.impression", {
        tags: {
            listId,
            listType,
            name,
            nrItems,
            sort,
            isRecentlyBoughtList,
            isCipList
        }
    });
}

export function trackPersonallistsOverview(nrOfLists: number, recentlyBoughtDisplayed: boolean, cipDisplayed: boolean) {
    trackStructuredEvent(nameString("overview"), {
        tags: {
            nrOfLists,
            recentlyBoughtDisplayed,
            cipDisplayed
        }
    });
}

export function trackPersonallistSelect(nrOfLists: number | undefined, listIndex: number | undefined) {
    trackStructuredEvent("list.select", {
        tags: {
            listIndex,
            nrOfLists
        }
    });
}

export function trackDeleteList(listType: string, listId: string, name: string) {
    trackStructuredEvent(nameString("deleteList"), {
        tags: {
            listType,
            listId,
            name
        }
    });
}

export function trackChangeCustomerVisibility(listType: string, listId: string, customerVisible: boolean) {
    trackStructuredEvent(nameString("changeCustomerVisibility"), {
        tags: {
            listType,
            listId,
            customerVisible
        }
    });
}

// DEPRECATED: Only used in old articlesearch-ui components anymore
export function trackCreateList(globallyVisible: boolean, readOnlyForEmployees: boolean) {
    trackStructuredEvent(nameString("createList"), { tags: { globallyVisible, readOnlyForEmployees } });
}

export function trackCreateListNew(
    bettyBundleId: string,
    bettyVariantId: string,
    name: string, // name of the list
    globallyVisible: boolean,
    readOnlyForEmployees: boolean,
    compulsoryTrackingTags: CompulsoryTrackingTags,
    optionalTrackingTags?: Partial<TrackingTags>
) {
    const tags = {
        ...optionalTrackingTags,
        originEventId: compulsoryTrackingTags?.originEventId || "",
        pageType: compulsoryTrackingTags?.pageType || "",
        bettyBundleId,
        bettyVariantId,
        globallyVisible,
        readOnlyForEmployees,
        name
    };
    trackStructuredEvent(nameString("createList"), { tags: tags });
}

export function trackPrintList(listType: string, listId: string, withPrices: boolean) {
    trackStructuredEvent(nameString("printList"), {
        tags: {
            listType,
            listId,
            withPrices
        }
    });
}

export function trackLoadMore(listType: string, listId: string, name: string) {
    trackStructuredEvent(nameString("loadMore"), {
        tags: {
            listType,
            listId,
            name
        }
    });
}

export function trackReplaceSearchLink(bundleId: string | undefined | null, replacementKind: ReplacementKind) {
    trackStructuredEvent(nameString("replaceSearchLink"), {
        tags: {
            bettyBundleId: bundleId,
            replacementKind
        }
    });
}

export function trackReplaceSearchLinkClose(bundleId: string | undefined | null, replacementKind: ReplacementKind) {
    trackStructuredEvent(nameString("replaceSearchLink.close"), {
        tags: {
            bettyBundleId: bundleId,
            replacementKind
        }
    });
}

export function trackReplacement(newItemId: string, oldItemId: string, replacementKind: ReplacementKind) {
    trackStructuredEvent(nameString("replacement"), { tags: { newItemId, oldItemId, replacementKind } });
}

export function trackExportCsv(listId: string) {
    trackStructuredEvent(nameString("exportCsv"), { tags: { listId } });
}

export function trackDeleteItem(
    bettyBundleId: string,
    bettyVariantId: string,
    listType: string,
    listId: string,
    name: string, // name of the list
    compulsoryTrackingTags: CompulsoryTrackingTags,
    optionalTrackingTags?: Partial<TrackingTags>
) {
    const tags = {
        ...optionalTrackingTags,
        originEventId: compulsoryTrackingTags?.originEventId || "",
        pageType: compulsoryTrackingTags?.pageType || "",
        bettyBundleId,
        bettyVariantId,
        listType,
        listId,
        name
    };
    trackStructuredEvent(nameString("deleteItem"), { tags: tags });
}

export function trackDeleteMultipleItem(listType: string, listId: string, bundleIds: string[]) {
    trackStructuredEvent(nameString("deleteMultipleItems"), {
        tags: {
            listType,
            listId,
            articleIds: bundleIds
        }
    });
}

export function trackListComponentView(listId: string, listType: string, name: string, nrItems: number) {
    trackStructuredEvent("list.componentView", {
        tags: {
            listId,
            listType,
            name,
            nrItems
        }
    });
}
